import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { UserModel } from 'src/app/models/authentication/user.model';
import { WalletBalanceRequestModel } from 'src/app/models/request/wallet-balance-request.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { WalletBalanceService } from 'src/app/services/wallet/wallet-balance.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-process-wallet-balance',
  templateUrl: './process-wallet-balance.component.html',
  styleUrls: ['./process-wallet-balance.component.scss'],
  standalone: false
})
export class ProcessWalletBalanceComponent implements OnInit, OnDestroy {
  @ViewChild('information') informationPopover!: Popover;

  formWalletBalance: FormGroup = new FormGroup({});
  listCompanies: any[] = [];
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Procesar Saldo Cartera';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = "process-wallet-balance";
  userAuthenticated: UserModel = new UserModel();

  constructor(private companyService: CompanyService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private walletBalanceService: WalletBalanceService,
    private windowService: WindowService) {
  }

  ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    this.getPermission();
    this.validateForm();
    this.getCompanies();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async getCompanies() {
    try {
      const [companies] = await Promise.all([
        firstValueFrom(this.companyService.getAllCompaniesForSelectAsync()),
      ]);
      this.listCompanies = companies;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos por tipo de impuesto.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
  }

  processWalletBalance() {
    Object.keys(this.formWalletBalance.controls).forEach(key => {
      const control = this.formWalletBalance.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formWalletBalance.valid) {
      const walletBalance = this.formWalletBalance.value as WalletBalanceRequestModel;
      const date = this.formWalletBalance.get("date")?.value;
      walletBalance.year = date.getFullYear();
      walletBalance.period = date.getMonth() + 1;

      let text;
      if (walletBalance.insertInvoices) {
        text = "Al procesar con el SW 'Inserta Facturas' se eliminará todos los datos que hay en saldo cartera actualmente. " +
          "¿Realmente desea procesar?";
      } else {
        text = "¿Realmente desea procesar?";
      }

      Swal.fire({
        title: 'Atención',
        text: text,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await firstValueFrom(this.walletBalanceService.processWalletBalance(walletBalance));
          this.toastHandlerService.showToast('success', `El proceso termino exitosamente.`);
        }
      });
    } else {
      this.showValidationMessages();
    }
  }

  openToggle(event: any) {
    this.informationPopover.toggle(event);
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatoria.',
        min: 'La compañía es obligatiria.',
      },
      date: {
        required: 'La fecha es obligatoria.',
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formWalletBalance.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formWalletBalance = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      date: ['', [Validators.required]],
      insertInvoices: [false, []]
    });
  }
}


